import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePlaces } from '@/hooks/usePlaces';
import { useRentalInfo } from '@/hooks/useRentalInfo';
import { useWeather } from '@/hooks/useWeather';
import { WifiQR } from '@/components/WifiQR';
import { OpenBadge } from '@/components/OpenBadge';
import { PlaceAvatar } from '@/components/PlaceAvatar';
import {
  SunIcon,
  HouseIcon,
  EmergencyIcon,
  ChevronRightIcon,
} from '@/components/Icons';
import { formatDriveTime } from '@/lib/formatting';
import { displayImageUrl } from '@shared/placeImages';
import type { PlaceWithState } from '@/types/api';

// Static house rules shown to guests. Editable later via rental_property
// entries with keys that start 'rule_' (not wired yet — keeping this simple).
const HOUSE_RULES: { label: string; body: string }[] = [
  { label: 'Quiet hours', body: '10 PM – 8 AM. Babcock Ranch is a residential neighborhood.' },
  { label: 'Pool & amenities', body: 'Babcock National pool and fitness require member access — check with us before heading over.' },
  { label: 'Parking', body: 'One covered assigned space plus one uncovered space. Please avoid parking on the grass.' },
];

function CompactPlaceRow({ place }: { place: PlaceWithState }) {
  return (
    <Link to={`/places/${place.slug}`} className="list-row">
      <PlaceAvatar
        name={place.name}
        slug={place.slug}
        category={place.category}
        imageUrl={displayImageUrl(place)}
        size={38}
        className="compact-place-row__avatar"
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        {place.name}
        <br />
        <small style={{ color: 'var(--color-ink-muted)' }}>{formatDriveTime(place.estimated_drive_minutes)} away</small>
      </span>
      <OpenBadge state={place.open_state} />
    </Link>
  );
}

export function GuestPage() {
  const { places } = usePlaces();
  const { info } = useRentalInfo();
  const { weather } = useWeather();

  // Pull wifi credentials from rental_property. Convention: keys 'wifi_ssid'
  // and 'wifi_password'. Falls back to a friendly placeholder card if not set.
  const wifi = useMemo(() => {
    const ssid = info?.find((r) => r.key === 'wifi_ssid')?.value;
    const password = info?.find((r) => r.key === 'wifi_password')?.value;
    if (!ssid || !password) return null;
    return { ssid, password };
  }, [info]);

  // Curated picks: prefer food + attraction categories, take the top 8.
  const picks = useMemo(() => {
    if (!places) return [];
    const scored = places
      .filter((p) => ['food', 'attraction', 'recreation'].includes(p.category))
      .slice(0, 8);
    return scored;
  }, [places]);

  const beaches = useMemo(
    () => (places ?? []).filter((p) => p.tags.includes('beach') || p.subcategory === 'beach').slice(0, 4),
    [places],
  );

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Welcome</h1>
        <p className="page-subtitle">You're staying at Babcock Ranch. Here's what you need.</p>
      </div>

      <div className="card">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <HouseIcon />
          Wi-Fi
        </h2>
        {wifi ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <WifiQR ssid={wifi.ssid} password={wifi.password} />
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--color-ink-muted)' }}>Network</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{wifi.ssid}</div>
              <div style={{ fontSize: 12, color: 'var(--color-ink-muted)', marginTop: 8 }}>Password</div>
              <div style={{ fontSize: 16, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{wifi.password}</div>
            </div>
            <p style={{ fontSize: 12, color: 'var(--color-ink-muted)', textAlign: 'center', margin: 0 }}>
              Point your camera at the code to join.
            </p>
          </div>
        ) : (
          <p style={{ color: 'var(--color-ink-muted)' }}>Wi-Fi credentials will appear here once your host sets them.</p>
        )}
      </div>

      {weather && (
        <div className="card">
          <h2>
            <SunIcon style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Right Now Outside
          </h2>
          <div style={{ fontSize: 40, fontWeight: 800, color: 'var(--color-pine-dark)' }}>{weather.temperatureF}°F</div>
          <p style={{ color: 'var(--color-ink-muted)' }}>
            {weather.conditions} · High {weather.highF}° / Low {weather.lowF}° · {weather.precipProbability}% rain
          </p>
        </div>
      )}

      <div className="card">
        <h2>House Rules</h2>
        {HOUSE_RULES.map((rule) => (
          <div key={rule.label} className="note-block">
            <div className="note-label">{rule.label}</div>
            {rule.body}
          </div>
        ))}
      </div>

      {picks.length > 0 && (
        <div className="card">
          <h2>Our Picks While You're Here</h2>
          <p style={{ color: 'var(--color-ink-muted)', fontSize: 13, marginTop: 4 }}>
            Personally recommended, not scraped from the internet.
          </p>
          <div style={{ marginTop: 8 }}>
            {picks.map((p) => (
              <CompactPlaceRow key={p.id} place={p} />
            ))}
          </div>
          <Link to="/places" className="btn btn-ghost btn-block" style={{ marginTop: 12 }}>
            All Places
          </Link>
        </div>
      )}

      {beaches.length > 0 && (
        <div className="card">
          <h2>Beach Shortcuts</h2>
          {beaches.map((p) => (
            <CompactPlaceRow key={p.id} place={p} />
          ))}
        </div>
      )}

      <Link to="/emergency" className="card" style={{ display: 'block', background: 'var(--color-pine)', color: '#fff' }}>
        <h2 style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
          <EmergencyIcon />
          Emergency
        </h2>
        <p style={{ color: 'var(--color-sand)', fontSize: 14, marginTop: 4 }}>911, nearest ER, and urgent contacts</p>
      </Link>

      <Link to="/" className="list-row" style={{ marginTop: 8 }}>
        <span style={{ color: 'var(--color-ink-muted)' }}>Full Babcock Nav (residents)</span>
        <ChevronRightIcon />
      </Link>
    </div>
  );
}
